import React, { useCallback, useEffect } from "react";
import { FC } from "react";
import { Button, Dialog, useTheme } from "@mui/material";
import { ContentCopyRounded } from "@mui/icons-material";
import { getPatientNameById } from "../../../../../services/patient.service";
import { Details } from "../../topic/products/product-detail";
import { OralModel } from "../../../../../models/Oral.model";
import { ConsultationPreviewCard, PreviewActive } from "../../../../../components/ui";
import VehiclesModel from "../../../../../models/vehicles.model";
import { getPeriodLabel } from "../../../../../utils/period-label";

export const oralModelToPreview = (oral: OralModel): Details => {
    return {
        id: Number(oral.id),
        name: oral.name,
        mechanism: oral.mechanism,
        description: oral.description,
        minimum: oral.min,
        maximum: oral.max,
        source: oral.source,
        unity: oral.unity,
        favorite: Boolean(oral.favorite),
        annotation: oral.annotation,
        classes: oral.classifications?.join(", "),
        actives: oral.userClassifications || [],
        percentage: oral.volume,
    };
};

const detailToPreviewActive = (detail: Details): PreviewActive => ({
    name: detail.name || "—",
    conc: detail.percentage ? `${detail.percentage} ${detail.unity || "%"}` : "—",
});

const firstName = (fullName: string): string => (fullName || "").trim().split(/\s+/)[0] || fullName;

type PreviewProps = {
    period?: string;
    patientId?: number;
    patientIds?: number[];
    consultationName: string;
    details: Details[];
    previewOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    vehicle?: Pick<VehiclesModel, 'name'> | null;
    observation?: string;
    observations?: string[];
}

export const Preview: FC<PreviewProps> = ({previewOpen, onClose, details, consultationName, period, patientId, patientIds, vehicle, observation, observations}) => {
    const [name, setName] = React.useState<string>('')
    const [copied, setCopied] = React.useState(false)
    const theme = useTheme()

    const fetchNames = useCallback(async (ids: number[]) => {
        if (ids.length > 1) {
            const results = await Promise.all(ids.map((id) => getPatientNameById(id)));
            setName(results.map((result) => firstName(result.data.name)).join(", "));
        } else if (ids.length === 1 && ids[0] !== 0) {
            const result = await getPatientNameById(ids[0]);
            setName(result.data.name);
        } else {
            setName('Não informado');
        }
    },[])

    useEffect(() =>{
        const ids = patientIds && patientIds.length > 0 ? patientIds : [patientId || 0];
        fetchNames(ids);
    },[fetchNames, patientId, patientIds])

    const meta = [consultationName || "Consulta sem nome", period ? getPeriodLabel(period) : null].filter(Boolean).join(" · ");

    const handleCopy = async () => {
        const formula = details.length > 0
            ? details.map(detailToPreviewActive).map((active) => `- ${active.name}: ${active.conc}`).join("\n")
            : "- Nenhum ativo nesta consulta.";
        const lines = [
            `Paciente: ${name || "Não informado"}`,
            `Consulta: ${consultationName || "Consulta sem nome"}`,
            period ? `Período: ${getPeriodLabel(period)}` : "",
            `Veículo: ${vehicle?.name || "Base não informada"}`,
            observation ? `Observação do veículo: ${observation}` : "",
            "",
            "Ativos:",
            formula,
            observations && observations.length > 0 ? "\nObservações:\n" + observations.join("\n") : "",
        ].filter(Boolean).join("\n");

        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(lines);
            } else {
                const textArea = document.createElement("textarea");
                textArea.value = lines;
                textArea.style.position = "fixed";
                textArea.style.opacity = "0";
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand("copy");
                document.body.removeChild(textArea);
            }
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1800);
        } catch {
            setCopied(false);
        }
    };

    return (
        <Dialog open={previewOpen} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: "18px", width: "100%", maxHeight: "calc(100vh - 32px)" } }}>
            <ConsultationPreviewCard
                patientName={name}
                meta={meta}
                actives={details.map(detailToPreviewActive)}
                vehicleName={vehicle?.name}
                vehicleObservation={observation}
                observations={observations}
                onClose={onClose}
                compact
                actions={
                    <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<ContentCopyRounded sx={{ fontSize: 16 }} />}
                        onClick={handleCopy}
                        sx={{ height: copied ? 48 : 38, minWidth: 0, px: 1.5, fontSize: copied ? "11px" : "12px", lineHeight: 1.1, fontWeight: 600, whiteSpace: "normal", borderColor: theme.palette.primary.main, color: "primary.main", "@media (min-height: 900px)": { height: copied ? 48 : 42, fontSize: copied ? "11px" : "13px" } }}
                    >
                        {copied ? "Copiado para a área de transferência" : "Copiar"}
                    </Button>
                }
            />
        </Dialog>
    )
}
